import { FC, useState } from 'react';
import { Formik } from 'formik';
import { Form } from 'react-router-dom';
import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';

import InformationIcon from '../../../../../../../assets/icons/Info, Infornation.png';

import CustomButton from '../../../../../../../common/CustomButton';
import ToggleRadioButton from '../../../../../../../common/ToggleRadioButton';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks/redux';
import { useUpdateUserAdditionalInfoMutation } from '../../../../../../../store/services/customizeOptionsApi';
import { setUserInfo } from '../../../../../../../store/slices/userSlice';
import { SoloIcon, LikeIcon, FamilyIcon, FriendsIcon, WorkTravelIcon } from '../../../../../../../components/Icons';
import { generateActiveGradient } from '../../../../../../../utils/gradientUtils';

import styles from './TravelFrom.module.scss';

type FormikValues = {
  travel?: string;
  collaborate?: string;
};

type TravelFromProps = {
  closeModal: () => void;
};

const TravelFrom: FC<TravelFromProps> = ({ closeModal }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user);
  const [updateUserAdditionalInfo, { isSuccess }] = useUpdateUserAdditionalInfoMutation();

  const [resErrors, setResErrors] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);

  const initialValues = {
    travel: `${user.travel_preference} travel`,
    collaborate: user.is_inviting_others ? 'yes' : 'no',
  };

  const handleSubmit = async (values: FormikValues) => {
    const payload = {
      travel_preference: values.travel?.split(' ')[0],
      is_inviting_others: values.collaborate === 'yes',
    };

    try {
      await updateUserAdditionalInfo(payload);
      dispatch(setUserInfo(payload));

      setResErrors(null);
      closeModal();
    } catch (error) {
      console.error('Error updating user info:', error);
      setResErrors((error as any)?.data?.errors || null);
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: FormikValues = {};

    return errors;
  };

  return (
    <Box className={styles.travel}>
      <Formik
        initialValues={initialValues}
        validate={formikValidate}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleSubmit, isSubmitting }) => (
          <Form
            className={styles.travelForm}
            onSubmit={handleSubmit}
          >
            <FormControl
              component="fieldset"
              className={styles.radioGroupWrapper}
            >
              <Typography
                variant="h5Bold"
                className={styles.stepTitle}
              >
                Let's personalize your AI assistant
              </Typography>
              <Typography
                variant="h10"
                className={styles.stepSubtitle}
              >
                How do you travel?
              </Typography>
              <RadioGroup
                name="travel"
                className={styles.radioGroup}
                value={values.travel}
                onChange={handleChange}
              >
                {[
                  { name: 'Solo travel', IconComponent: SoloIcon, iconSize: 45 },
                  { name: 'Couples travel', IconComponent: LikeIcon, iconSize: 45 },
                  { name: 'Family travel', IconComponent: FamilyIcon, iconSize: 45 },
                  { name: 'Friends travel', IconComponent: FriendsIcon, iconSize: 45 },
                  { name: 'Work travel', IconComponent: WorkTravelIcon, iconSize: 45 },
                ].map(val => {
                  const { IconComponent, iconSize } = val;
                  const isSelected = values.travel === val.name;
                  return (
                    <FormControlLabel
                      key={val.name}
                      value={val.name}
                      control={<Radio sx={{ display: 'none' }} />}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '16px',
                            ...(isSelected
                              ? {
                                  background:
                                    'linear-gradient(white, white) padding-box, linear-gradient(231.69deg, #15C3FF 20.67%, #8DF4DB 100%) border-box',
                                  border: '2px solid transparent',
                                }
                              : {
                                  border: '1px solid #BDC9EE',
                                }),
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            width: '200px',
                            height: '130px',
                            gap: '8px',
                          }}
                        >
                          <IconComponent
                            size={iconSize}
                            color={isSelected ? undefined : '#BDC9EE'}
                            gradient={isSelected ? generateActiveGradient(val.name, 'travel') : undefined}
                          />
                          <Typography
                            variant="h10"
                            sx={{ textAlign: 'center' }}
                          >
                            {val.name.replace('_', ' ')}
                          </Typography>
                        </Box>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>

            <FormControl
              component="fieldset"
              className={`${styles.radioGroupWrapper} ${styles.mt35}`}
            >
              <FormLabel component="legend">Do you plan to invite others to collaborate?</FormLabel>
              <RadioGroup
                name="collaborate"
                className={styles.radioGroup}
                value={values.collaborate}
                onChange={handleChange}
              >
                {['yes', 'no'].map(val => (
                  <FormControlLabel
                    key={val}
                    value={val}
                    control={<Radio sx={{ display: 'none' }} />}
                    label={
                      <ToggleRadioButton
                        value={val}
                        label={val}
                        selected={values.collaborate === val}
                      />
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <Box className={styles.additionalInfo}>
              <Typography
                variant="h11"
                className={styles.additionalInfoText}
              >
                To get badges, fill in all the questions
              </Typography>
              <img
                src={InformationIcon}
                className={styles.additionalInfoImg}
                alt="info"
              />
            </Box>

            <Box className={styles.cityAndAiroportBtns}>
              <CustomButton
                btnType="button"
                variant="outlined"
                onClick={closeModal}
              >
                Cancel
              </CustomButton>
              <CustomButton
                btnType="submit"
                customClass={styles.customButton}
                disabled={isSubmitting}
              >
                Save
              </CustomButton>
            </Box>
            {isSuccess && (
              <Typography
                variant="h12"
                sx={{ color: '#208B64' }}
              >
                Travel style updated successfully.
              </Typography>
            )}
            {resErrors &&
              resErrors.map((error, index) => (
                <Typography
                  key={index}
                  variant="h12"
                  sx={{ color: '#C8102E' }}
                >
                  {error.detail}
                </Typography>
              ))}
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default TravelFrom;
